import {
	formatDateUS,
	formatTimeUS,
	formatVisitDate,
} from "./calendarDate";

const GPS_FLAG_MARKERS = ["GPS", "Location"];

const getNestedLocation = (location) => {
	if (!location || typeof location !== "object") {
		return { start: null, end: null };
	}
	if (location.startLocation || location.endLocation) {
		return {
			start: location.startLocation || null,
			end: location.endLocation || null,
		};
	}
	return { start: location, end: location };
};

const isVerificationSuccessful = (visit) => {
	const method = String(visit?.method_status || "").toLowerCase();
	if (!method) return false;
	if (method === "sms") return Boolean(visit.verification_code);
	if (method === "photo") return Boolean(visit.verification_photo);
	if (method === "audio") return Boolean(visit.verification_audio);
	if (method === "member_card") return Boolean(visit.confirmation_id || visit.confirmation);
	return Boolean(visit.verification_code || visit.verification_photo || visit.verification_audio);
};

const getFlagReasons = (visit) => {
	if (Array.isArray(visit?.flagReasons)) return visit.flagReasons.filter(Boolean);
	if (Array.isArray(visit?.flag_reasons)) return visit.flag_reasons.filter(Boolean);
	return [];
};

const getGpsMissingState = (visit) => {
	const { start, end } = getCoordinatePair(visit?.location);
	const hasStart = Boolean(start);
	const hasEnd = Boolean(end);
	return {
		hasEnd,
		hasStart,
		isFullyMissing: !hasStart && !hasEnd,
		missingEnd: hasStart && !hasEnd,
		missingStart: !hasStart && hasEnd,
	};
};

const getGpsDisplayLabel = (visit) => {
	const { isFullyMissing, missingEnd, missingStart } = getGpsMissingState(visit);
	if (isFullyMissing || missingStart || missingEnd) return "Missing";
	const status = getGpsStatus(visit);
	if (status === "Valid") return "Valid";
	if (status === "Invalid") return "Invalid";
	return status || "—";
};

const getGpsAlertMessage = (visit) => {
	const { isFullyMissing, missingEnd, missingStart } = getGpsMissingState(visit);
	if (isFullyMissing) {
		return "GPS data was not captured. Device location may have been disabled.";
	}
	if (missingStart && missingEnd) {
		return "GPS data was not captured. Device location may have been disabled.";
	}
	if (missingStart) return "Start location GPS data is missing.";
	if (missingEnd) return "End location GPS data is missing.";
	return null;
};

const getConfirmationMethodDisplay = (visit) => {
	if (getConfirmationStatus(visit) === "Confirmed") {
		return getVerificationMethodLabel(visit);
	}
	const method = getVerificationMethodLabel(visit);
	if (method && method !== "—") return method;
	return "—";
};

const getVerificationAtDisplay = (visit) => {
	if (getConfirmationStatus(visit) === "Confirmed") {
		return getVerificationDateTime(visit);
	}
	if (getConfirmationStatus(visit) === "Unconfirmed") {
		return "Not completed";
	}
	return "—";
};

const isFlaggedVisit = (visit) => getVisitStatus(visit) === "Flagged";

const getVisitStatus = (visit) => {
	if (visit?.visitStatus) return visit.visitStatus;
	if (visit?.verification_status === "Flagged") return "Flagged";

	const isComplete =
		visit?.is_completed === true ||
		visit?.verification_status === "Verified" ||
		isVerificationSuccessful(visit);

	if (isComplete) {
		if (getFlagReasons(visit).length) return "Flagged";
		return "Verified";
	}

	return "Incomplete";
};

const getConfirmationStatus = (visit) => {
	if (visit?.confirmationStatus) return visit.confirmationStatus;
	if (visit?.is_completed === true || visit?.verification_status === "Verified" || isVerificationSuccessful(visit)) {
		return "Confirmed";
	}
	if (visit?.time_in || visit?.timeIn) return "Unconfirmed";
	return null;
};

const hasGpsIssue = (visit, reasons = getFlagReasons(visit)) =>
	reasons.some((reason) => GPS_FLAG_MARKERS.some((marker) => String(reason).includes(marker)));

const getGpsStatus = (visit) => {
	if (visit?.gpsStatus) return visit.gpsStatus;
	const reasons = getFlagReasons(visit);
	const { start, end } = getNestedLocation(visit?.location);
	const hasEnded = Boolean(visit?.time_out || visit?.timeOut);

	if (!start || !end) {
		if (hasEnded || hasGpsIssue(visit, reasons)) return "Invalid";
		return null;
	}
	if (hasGpsIssue(visit, reasons)) return "Invalid";
	return "Valid";
};

const formatVisitDisplayId = (visit) => {
	if (visit?.visitDisplayId) return visit.visitDisplayId;
	const id = visit?.id ?? visit?.visit_id;
	if (id == null || id === "") return "—";
	return `CV-${id}`;
};

const formatDurationLabel = (visit) => {
	if (visit?.durationLabel) return visit.durationLabel;
	const minutes = visit?.duration_minutes ?? visit?.duration;
	if (minutes == null || minutes === "") return "—";
	const totalMinutes = Number(minutes);
	if (Number.isNaN(totalMinutes) || totalMinutes < 0) return "—";
	if (totalMinutes === 0) return "0m";
	const hours = Math.floor(totalMinutes / 60);
	const remainingMinutes = totalMinutes % 60;
	if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
	if (hours) return `${hours}h`;
	return `${remainingMinutes}m`;
};

const getFlagCount = (visit) => {
	if (typeof visit?.flagCount === "number") return visit.flagCount;
	return getFlagReasons(visit).length;
};

const formatFlagLabel = (visit) => {
	const count = getFlagCount(visit);
	if (!count) return "—";
	if (count === 1) return "1 Flag";
	return `${count} Flags`;
};

const getClientName = (visit) => {
	const client = visit?.client;
	return [client?.firstName, client?.lastName].filter(Boolean).join(" ").trim() || client?.clientid || "—";
};

const getProviderName = (visit) => {
	const user = visit?.user;
	return [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim() || user?.userId || user?.email || "—";
};

const formatDateTimeUS = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleString("en-US", {
		month: "numeric",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
};

const getVerificationMethodLabel = (visit) => {
	if (visit?.verificationMethodLabel) return visit.verificationMethodLabel;
	const method = String(visit?.method_status || "").trim().toLowerCase();
	if (method === "sms") return "SMS Verification Code";
	if (method === "photo") return "Photo Verification";
	if (method === "audio") return "Audio Verification";
	if (method === "member_card") return "TeleTrace Member Card";
	return visit?.method_status || "—";
};

const getClientPhoneNumber = (visit) => visit?.clientPhoneNumber || visit?.client?.phone_number || null;

const getCompletedInterventions = (visit) => {
	if (Array.isArray(visit?.completedInterventions)) return visit.completedInterventions;
	if (!Array.isArray(visit?.reason)) return [];
	return visit.reason
		.map((item) => {
			if (typeof item === "string") return item.trim();
			if (item?.text) return String(item.text).trim();
			return null;
		})
		.filter(Boolean);
};

const getCoordinatePair = (location) => {
	const { start, end } = getNestedLocation(location);
	const toPair = (point) => {
		if (!point) return null;
		const lat = Number(point.lat);
		const lng = Number(point.long ?? point.lng);
		if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
		return { lat, lng };
	};
	return { start: toPair(start), end: toPair(end) };
};

const formatCoordinateDegrees = (point) => {
	if (!point) return "—";
	const lat = Number(point.lat);
	const lng = Number(point.long ?? point.lng);
	if (Number.isNaN(lat) || Number.isNaN(lng)) return "—";
	const latDirection = lat >= 0 ? "N" : "S";
	const lngDirection = lng >= 0 ? "E" : "W";
	return `${Math.abs(lat).toFixed(4)}° ${latDirection}, ${Math.abs(lng).toFixed(4)}° ${lngDirection}`;
};

const formatCoordinateDegreesLabel = (visit, key) => {
	const { start, end } = getCoordinatePair(visit?.location);
	const point = key === "start" ? start : end;
	if (point) return formatCoordinateDegrees(point);
	if (key === "start" && visit?.startCoordinates) return visit.startCoordinates;
	if (key === "end" && visit?.endCoordinates) return visit.endCoordinates;
	return "—";
};

const formatCoordinateLabel = (visit, key) => {
	const { start, end } = getCoordinatePair(visit?.location);
	const point = key === "start" ? start : end;
	if (!point) return "—";
	return `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
};

const getGpsDistanceLabel = (visit) => {
	if (visit?.gpsDistanceMiles != null) {
		const miles = Number(visit.gpsDistanceMiles);
		if (!Number.isNaN(miles)) {
			if (miles < 0.01) return `${Math.round(miles * 5280)} feet apart`;
			return `${miles.toFixed(1)} miles apart`;
		}
	}
	if (visit?.gpsDistance) {
		const label = String(visit.gpsDistance);
		return label.includes("apart") ? label : `${label} apart`;
	}
	return "—";
};

const getGpsAccuracyLabel = (visit) => {
	const raw = visit?.gpsAccuracy;
	if (!raw) return "—";
	const match = String(raw).match(/([\d.]+)\s*m/i);
	if (match) {
		const meters = Math.round(Number(match[1]));
		return Number.isNaN(meters) ? raw : `±${meters} meters`;
	}
	return String(raw).startsWith("±") ? raw : `±${raw}`;
};

const getVerificationDateTime = (visit) => {
	const raw = visit?.verificationDateTime || (
		visit?.verification_status === "Verified" || getConfirmationStatus(visit) === "Confirmed"
			? visit?.time_out || visit?.updated_at || visit?.time_in
			: null
	);
	if (!raw) return "—";
	const date = new Date(raw);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
};

export {
	formatCoordinateDegreesLabel,
	formatCoordinateLabel,
	formatDateTimeUS,
	formatDateUS,
	formatDurationLabel,
	formatFlagLabel,
	formatTimeUS,
	formatVisitDate,
	formatVisitDisplayId,
	getClientName,
	getClientPhoneNumber,
	getCompletedInterventions,
	getConfirmationMethodDisplay,
	getConfirmationStatus,
	getCoordinatePair,
	getFlagCount,
	getFlagReasons,
	getGpsAccuracyLabel,
	getGpsAlertMessage,
	getGpsDisplayLabel,
	getGpsDistanceLabel,
	getGpsMissingState,
	getGpsStatus,
	getProviderName,
	getVerificationAtDisplay,
	getVerificationDateTime,
	getVerificationMethodLabel,
	getVisitStatus,
	isFlaggedVisit,
};
